"use client";

import React, { useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { CountrySelectorChildrenProps } from "~/bff/components/CountrySelectorChildren";
import { Image } from "~/components/image/component";
import { CountryCode } from "~/helpers/country/types";
import { prepareTextAttribute } from "~/helpers/prepare-text-attribute";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Down24 } from "~/theme/icons/components/down";
import { getImageSrc } from "~/utils/image/get-image-src";

import { CountrySelectorPopup } from "./components/country-selector-popup/component";
import { COUNTRY_SELECTOR_CLASSES } from "./constants";
import { CountrySelectorContainer } from "./styled";

export interface CountrySelectorPropsExtended {
  value?: CountryCode;
  selectedLanguage?: string;
  onChange?: (countryCode?: CountryCode, countryLanguageCode?: string) => void;
  countries?: (CountrySelectorChildrenProps | undefined)[];
  className?: string;
  title?: string;
  description?: string;
  testAutomationId?: string;
}

export const CountrySelector: React.FC<CountrySelectorPropsExtended> = ({
  value,
  onChange,
  countries,
  className,
  title,
  description,
  selectedLanguage,
  testAutomationId,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedCountry = useMemo(() => {
    return countries?.find(
      (el) =>
        value?.toLowerCase() === el?.countryKey?.toLowerCase() &&
        selectedLanguage?.toLowerCase() === el?.languageCode?.toLowerCase(),
    );
  }, [countries, selectedLanguage, value]);

  const handleTogglePopup = useCallback((switcher: boolean) => {
    setIsOpen(switcher);
  }, []);
  const handleChange = useCallback(
    (countryCode?: CountryCode, countryLanguageCode?: string) => {
      if (onChange) {
        onChange(countryCode, countryLanguageCode);
        setIsOpen(false);
      }
    },
    [onChange],
  );

  return (
    <>
      <CountrySelectorContainer
        onClick={() => handleTogglePopup(true)}
        className={className}
        tabIndex={0}
        {...getTestAutomationProps(
          testAutomationId ?? "country-selector-popup-button",
        )}
        id="country-selector-popup-button"
        aria-label={`${selectedCountry?.link?.label}, click to change the country`}
      >
        <Box className={COUNTRY_SELECTOR_CLASSES.icon}>
          <Image
            src={getImageSrc(selectedCountry?.image?.source ?? undefined)}
            alt={prepareTextAttribute(selectedCountry?.image?.alt, true)}
            title={prepareTextAttribute(selectedCountry?.image?.title)}
          />
        </Box>
        <Typography
          variant="body1"
          className={COUNTRY_SELECTOR_CLASSES.currentCountryLabel}
        >
          {selectedCountry?.link?.label}
        </Typography>
        <Box className={COUNTRY_SELECTOR_CLASSES.downIcon}>
          <Down24 />
        </Box>
      </CountrySelectorContainer>
      <CountrySelectorPopup
        title={title}
        description={description}
        onClick={handleChange}
        onClose={handleTogglePopup}
        countries={countries}
        value={value}
        className={className}
        isOpen={isOpen}
      />
    </>
  );
};
